import Hls from "hls.js";

export enum MediaFileType {
  M3U8 = "M3U8",
  MP3 = "MP3",
  MP4 = "MP4",
}

export enum MediaMimeType {
  M3U8 = "application/vnd.apple.mpegurl",
  MP3 = "audio/mp3",
  MP4 = "video/mp4",
}

export type MediaElement = HTMLAudioElement | HTMLVideoElement;

export const MediaMimeTypeMap = new Map(
  Object.entries({
    [MediaFileType.M3U8]: MediaMimeType.M3U8,
    [MediaFileType.MP4]: MediaMimeType.MP4,
    [MediaFileType.MP3]: MediaMimeType.MP3,
  }),
) as Map<keyof typeof MediaFileType, MediaMimeType>;

/**
 * Determines the mime type of the media source
 */
export function getMimeTypeFromUrl(src: string) {
  const ext = src.match(/[^/.\s]+$/)?.[0].toUpperCase() as MediaFileType;

  return MediaMimeTypeMap.get(ext) ?? MediaMimeTypeMap.get(MediaFileType.M3U8);
}

/**
 * Loads the media file into the player element.
 */
export function load(mediaElement: MediaElement, props: { src?: string }) {
  const { src } = props;

  if (!src || !src.length) {
    return;
  }

  const willUseNative = shouldUseNative(src, mediaElement);

  if (willUseNative) {
    if (props.src) {
      mediaElement.setAttribute("src", src);
      mediaElement.load();
    } else {
      mediaElement.removeAttribute("src");
    }
  } else {
    const hls = new Hls();

    hls.loadSource(src);
    hls.attachMedia(mediaElement);
  }
}

/**
 * Determines whether or not to use the native platform to
 * load the media source.
 */
export function shouldUseNative(src: string, mediaElement: MediaElement) {
  const mimetype = getMimeTypeFromUrl(src);

  if (!mimetype || mimetype !== MediaMimeTypeMap.get(MediaFileType.M3U8)) {
    return true;
  }

  return Boolean(mediaElement.canPlayType(mimetype));
}
